import React, { useEffect, useState, useRef } from 'react';
import { MapPin } from 'lucide-react';

function TelecomPartners() {
  const [visibleElements, setVisibleElements] = useState([]);
  const componentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });

  // Adjusted partner positions to prevent overlapping
  const partners = [
    { id: 1, name: 'T-Mobile', logo: '/t-mobile.png', left: '10%', top: '10%', width: '100px' },
    { id: 2, name: 'Verizon', logo: '/verizon.jpg', left: '25%', top: '15%', width: '100px' },
    { id: 3, name: 'AT&T', logo: '/at.svg', left: '40%', top: '10%', width: '100px' },
    { id: 4, name: '8x8', logo: '/8x8.jpg', left: '75%', top: '15%', width: '80px' },
    { id: 5, name: 'Zoom', logo: '/zoom.svg', left: '60%', top: '10%', width: '100px' },
    { id: 6, name: 'NEC', logo: '/NEC.png', left: '90%', top: '15%', width: '90px' },
    { id: 7, name: 'Metro', logo: 'Metro.svg', left: '15%', top: '45%', width: '90px' },
    { id: 8, name: 'Airespring', logo: '/airespring.png', left: '30%', top: '50%', width: '100px' },
    { id: 9, name: 'Spectrum', logo: '/spectrum.png', left: '45%', top: '45%', width: '100px' },
    { id: 10, name: 'IPFone', logo: '/ipfone.png', left: '65%', top: '50%', width: '90px' },
    { id: 11, name: 'Webex', logo: '/Webex.png', left: '80%', top: '45%', width: '100px' },
    { id: 12, name: 'RingCentral', logo: '/ringcentral.svg', left: '20%', top: '75%', width: '100px' },
    { id: 13, name: 'Frontier', logo: '/jbs.png', left: '40%', top: '68%', width: '90px' },
    { id: 14, name: 'Five9', logo: '/five9.svg', left: '60%', top: '70%', width: '90px' },
    { id: 15, name: 'Genesys', logo: '/genesys.svg', left: '80%', top: '75%', width: '100px' },
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      const rect = componentRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        setMousePosition({ x, y });
      }
    };

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const handleIntersection = (entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && !isVisible) {
        setVisibleElements([]);
        setIsVisible(true);
        partners.forEach((partner, index) => {
          setTimeout(() => {
            setVisibleElements(prev => [...prev, partner.id]);
          }, 100 * index);
        });
      } else if (!entry.isIntersecting && isVisible) {
        setIsVisible(false);
        setVisibleElements([]);
      }
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);

    if (componentRef.current) {
      observer.observe(componentRef.current);
      componentRef.current.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
        componentRef.current.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [isVisible]);

  const outerContainerStyle = {
    width: '100%',
    maxWidth: '100vw',
    overflowX: 'hidden',
    marginBottom:'100px',
    position: 'relative'
  };

  const containerStyle = {
    width: '100%',
    maxWidth: '100%',
    position: 'relative',
    background: 'linear-gradient(to bottom, rgba(255,255,255,0.97), rgba(248,249,250,0.97))',
    overflow: 'hidden',
    perspective: '1500px'
  };
  const titleStyle = {
    fontSize: 'clamp(2.5rem, 5vw, 3.125rem)', // 40px to 50px
    color: '#410684',
    fontWeight: '600',
    lineHeight: '1.2',
    fontFamily: 'Oswald, sans-serif',
    marginBottom: 'clamp(1rem, 2vw, 1.25rem)',
    textAlign: 'center',
    letterSpacing: '-0.03em',
    textShadow: '1px 1px 1px rgba(0, 0, 0, 0.1)',
    padding: 'clamp(1.5rem, 4vw, 2.5rem) clamp(1rem, 2vw, 1.25rem) 0',
    opacity: isVisible ? '1' : '0',
    transform: isVisible ? 'translateY(0) rotateX(0)' : 'translateY(20px) rotateX(-10deg)',
    transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
  };

  const mapContainerStyle = {
    position: 'relative',
    width: '100%',
    height: '350px',
    background: `url('/map.jpg')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    marginTop: '20px',
    transform: `rotateX(${(mousePosition.y - 0.5) * 5}deg) rotateY(${(mousePosition.x - 0.5) * 5}deg)`,
    transition: 'transform 0.3s ease-out',
    transformStyle: 'preserve-3d'
  };

  const overlayContainerStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transformStyle: 'preserve-3d'
  };

  const get3DTransform = (x, y) => {
    const centerX = x - 0.5;
    const centerY = y - 0.5;
    return `rotateX(${-centerY * 20}deg) rotateY(${centerX * 20}deg)`;
  };

  const locationGroupStyle = (partner) => ({
    position: 'absolute',
    left: partner.left,
    top: partner.top,
    display: 'flex',
    flexDirection: 'column-reverse', // Changed to column-reverse to put logo above pin
    alignItems: 'center',
    transform: visibleElements.includes(partner.id)
      ? `translate(-50%, 0) scale(1) translateZ(50px) ${get3DTransform(mousePosition.x, mousePosition.y)}`
      : `translate(-50%, -20px) scale(0.8) translateZ(0px)`,
    opacity: visibleElements.includes(partner.id) ? '1' : '0',
    transition: 'all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transformStyle: 'preserve-3d'
  });

  const pinStyle = {
    color: '#410684',
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
    marginTop: '-5px', // Adjusted to connect with logo
    zIndex: 1,
    transform: 'translateZ(30px) scale(0.8)',
    transition: 'all 0.3s ease-out'
  };

  const logoContainerStyle = (partner) => ({
    width: partner.width,
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '12px',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transform: 'translateZ(40px)',
    transition: 'all 0.3s ease-out',
    position: 'relative',
    zIndex: 2
  });

  const logoStyle = {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    maxHeight: '40px',
    transition: 'all 0.3s ease-out',
    filter: 'grayscale(0%)'
  };

  return (
    <div style={outerContainerStyle}>
      <div style={containerStyle} ref={componentRef}>
        <h1 style={titleStyle}>
          OUR TRUSTED TECH PARTNERS
        </h1>
        <div style={mapContainerStyle}>
          <div style={overlayContainerStyle}>
            {partners.map((partner) => (
              <div 
                key={partner.id} 
                style={locationGroupStyle(partner)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = `translate(-50%, -10px) scale(1.1) translateZ(70px) ${get3DTransform(mousePosition.x, mousePosition.y)}`;
                  const pin = e.currentTarget.querySelector('.map-pin');
                  if (pin) pin.style.transform = 'translateZ(50px) scale(1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = `translate(-50%, 0) scale(1) translateZ(50px) ${get3DTransform(mousePosition.x, mousePosition.y)}`;
                  const pin = e.currentTarget.querySelector('.map-pin');
                  if (pin) pin.style.transform = 'translateZ(30px) scale(0.8)';
                }}
              >
                <div style={logoContainerStyle(partner)}>
                  <img
                    src={partner.logo}
                    alt={`${partner.name} logo`}
                    style={logoStyle}
                    onError={(e) => {
                      e.target.src = '/placeholder-logo.png';
                    }}
                  />
                </div>
                <MapPin 
                  size={32} 
                  strokeWidth={2.5}
                  style={pinStyle}
                  className="map-pin"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TelecomPartners;